import {
  calculateCompositePricing,
  CompositePricingInput,
} from './feeCategoryCompositePricing';
import { FeeTierData } from '../api/feeCategoriesApi';
import { CompositePricingTextField } from '../components/FeeCategories/CompositePricingFields';

export type TierCommonSettings = Pick<
  CompositePricingInput,
  | 'extraFee1Percent'
  | 'extraFee1VatPercent'
  | 'extraFee1Fixed'
  | 'extraFee2Percent'
  | 'extraFee2VatPercent'
  | 'extraFee2Fixed'
> & {
  extraFee1Name?: string | null;
  extraFee2Name?: string | null;
};

export type TierLevelField =
  | 'basePrice'
  | 'vatAmount'
  | 'extraFee1Amount'
  | 'extraFee1VatAmount'
  | 'extraFee2Amount'
  | 'extraFee2VatAmount'
  | 'totalUnitPrice';

export type TierCommonField =
  | 'extraFee1Percent'
  | 'extraFee1VatPercent'
  | 'extraFee1Fixed'
  | 'extraFee2Percent'
  | 'extraFee2VatPercent'
  | 'extraFee2Fixed';

export function mergeTierCompositePricing(
  tier: FeeTierData,
  common: TierCommonSettings,
  vatPercentage: number,
): CompositePricingInput {
  return {
    basePrice: typeof tier.basePrice === 'number' ? tier.basePrice : parseFloat(String(tier.basePrice || 0)) || 0,
    vatAmount: tier.vatAmount,
    vatPercentage,
    extraFee1Percent: common.extraFee1Percent,
    extraFee1VatPercent: common.extraFee1VatPercent,
    extraFee1Fixed: common.extraFee1Fixed,
    extraFee2Percent: common.extraFee2Percent,
    extraFee2VatPercent: common.extraFee2VatPercent,
    extraFee2Fixed: common.extraFee2Fixed,
    extraFee1Amount: tier.extraFee1Amount,
    extraFee1VatAmount: tier.extraFee1VatAmount,
    extraFee2Amount: tier.extraFee2Amount,
    extraFee2VatAmount: tier.extraFee2VatAmount,
    totalUnitPrice: tier.totalUnitPrice,
    price: tier.price,
  };
}

export function recalculateTierWithCommon(
  tier: FeeTierData,
  common: TierCommonSettings,
  vatPercentage: number,
  changedField?: TierLevelField,
): FeeTierData {
  const resetAmounts = changedField === 'basePrice';
  const tierForCalc = resetAmounts
    ? {
        ...tier,
        vatAmount: undefined,
        extraFee1Amount: undefined,
        extraFee1VatAmount: undefined,
        extraFee2Amount: undefined,
        extraFee2VatAmount: undefined,
        totalUnitPrice: undefined,
      }
    : tier;

  const breakdown = calculateCompositePricing(
    mergeTierCompositePricing(tierForCalc, common, vatPercentage),
  );

  if (changedField === 'totalUnitPrice' && tier.totalUnitPrice != null) {
    return { ...tier, price: tier.totalUnitPrice };
  }

  return {
    ...tier,
    price: breakdown.totalUnitPrice,
    basePrice: tier.basePrice ?? breakdown.basePrice,
    vatAmount: resetAmounts ? breakdown.vatAmount : (tier.vatAmount ?? breakdown.vatAmount),
    extraFee1Amount: resetAmounts ? breakdown.extraFee1Amount : (tier.extraFee1Amount ?? breakdown.extraFee1Amount),
    extraFee1VatAmount: resetAmounts ? breakdown.extraFee1VatAmount : (tier.extraFee1VatAmount ?? breakdown.extraFee1VatAmount),
    extraFee2Amount: resetAmounts ? breakdown.extraFee2Amount : (tier.extraFee2Amount ?? breakdown.extraFee2Amount),
    extraFee2VatAmount: resetAmounts ? breakdown.extraFee2VatAmount : (tier.extraFee2VatAmount ?? breakdown.extraFee2VatAmount),
    totalUnitPrice: changedField === 'totalUnitPrice' ? tier.totalUnitPrice : undefined,
  };
}

export function recalculateAllTiers(
  tiers: FeeTierData[],
  common: TierCommonSettings,
  vatPercentage: number,
): FeeTierData[] {
  return tiers.map((tier) => recalculateTierWithCommon(tier, common, vatPercentage));
}

export function extractTierCommonSettings(source: TierCommonSettings): TierCommonSettings {
  return {
    extraFee1Percent: source.extraFee1Percent ?? 0,
    extraFee1VatPercent: source.extraFee1VatPercent ?? 0,
    extraFee1Fixed: source.extraFee1Fixed ?? 0,
    extraFee1Name: source.extraFee1Name ?? '',
    extraFee2Percent: source.extraFee2Percent ?? 0,
    extraFee2VatPercent: source.extraFee2VatPercent ?? 0,
    extraFee2Fixed: source.extraFee2Fixed ?? 0,
    extraFee2Name: source.extraFee2Name ?? '',
  };
}

export function parseTierUsageTo(
  value: string | number | null | undefined,
  options?: { isLastTier?: boolean },
): number | null {
  if (value === '' || value == null || value === undefined) {
    return null;
  }
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (isNaN(num) || num <= 0) {
    return options?.isLastTier ? null : 0;
  }
  return num;
}

export function formatTierUsageToForDisplay(
  value: number | null | undefined,
  options?: { isLastTier?: boolean },
): string | number {
  if (options?.isLastTier && (value == null || value === 0)) {
    return '';
  }
  return value ?? '';
}

export function buildTierSubmitPayload(
  tier: FeeTierData,
  common: TierCommonSettings,
  vatPercentage: number,
  options?: { isLastTier?: boolean },
) {
  const normalized: FeeTierData = {
    tierLevel: tier.tierLevel,
    usageFrom: typeof tier.usageFrom === 'string' ? parseFloat(tier.usageFrom) || 0 : tier.usageFrom || 0,
    usageTo: parseTierUsageTo(tier.usageTo, options),
    price: typeof tier.price === 'string' ? parseFloat(tier.price) || 0 : tier.price || 0,
    basePrice: tier.basePrice,
    vatAmount: tier.vatAmount,
    unit: tier.unit,
    extraFee1Amount: tier.extraFee1Amount,
    extraFee1VatAmount: tier.extraFee1VatAmount,
    extraFee2Amount: tier.extraFee2Amount,
    extraFee2VatAmount: tier.extraFee2VatAmount,
    totalUnitPrice: tier.totalUnitPrice,
  };

  const breakdown = calculateCompositePricing(mergeTierCompositePricing(normalized, common, vatPercentage));

  return {
    tierLevel: normalized.tierLevel,
    usageFrom: normalized.usageFrom,
    usageTo: normalized.usageTo,
    unit: normalized.unit?.trim() ? normalized.unit : undefined,
    price: tier.totalUnitPrice ?? breakdown.totalUnitPrice,
    basePrice: normalized.basePrice ?? breakdown.basePrice,
    vatAmount: normalized.vatAmount ?? breakdown.vatAmount,
    extraFee1Amount: normalized.extraFee1Amount ?? breakdown.extraFee1Amount,
    extraFee1VatAmount: normalized.extraFee1VatAmount ?? breakdown.extraFee1VatAmount,
    extraFee2Amount: normalized.extraFee2Amount ?? breakdown.extraFee2Amount,
    extraFee2VatAmount: normalized.extraFee2VatAmount ?? breakdown.extraFee2VatAmount,
  };
}
